import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    IApplicant, IApplicantRatingUpdate, IApplicantStageParams, IApplicantSummary, IApplicantSummaryParams,
    IApplicantsApiParams, IApplicantsListResponse, IApplicantsListState
} from 'Components/Applicants/ApplicantsModel';
import { AxiosResponse } from 'axios';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { IBaseResponse, IFailureResponse } from 'shared/SharedModels';
import { DefaultAPIErrorMsg, DefaultPageSize, ENDPOINTS } from 'shared/constants';

const initialApplicantsState: IApplicantsListState = {
    getAppliListStatus: 'idle',
    getAppliListResponse: '',
    applicantsList: [],
    paginationParams: {
        count: 0, requestedPageNumber: 1, requestedPageSize: DefaultPageSize, totalPages: 0
    },
    applicantsSummary: {}
};

export const fetchApplicantsList = createAsyncThunk<IApplicantsListResponse, IApplicantsApiParams, { rejectValue: IBaseResponse }>(
    'applicantsList',
    async ({ jobId, pgNo, pgSize, sortCol, sortDir, search, finalMatches, stages, keywords, ratings }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${ENDPOINTS.EMPLOYER_JOBS}/${jobId}/applicants?pageNo=${pgNo}&pageSize=${pgSize}&sortColumn=${sortCol}&sortDirection=${sortDir}${search ? `&search=${search}` : ''}${finalMatches ? `&finalMatches=${finalMatches}` : ''}${stages ? `&stages=${stages}` : ''}${keywords ? `&keywords=${keywords}` : ''}${ratings ? `&ratings=${ratings}` : ''} `)
            .then((response: AxiosResponse<IApplicantsListResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const fetchApplicantSummary = createAsyncThunk<IBaseResponse<IApplicantSummary>, IApplicantSummaryParams, { rejectValue: IBaseResponse }>(
    'applicantSummary',
    async ({ jobId, candidateEmployerJobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${ENDPOINTS.APPLICANT_SUMMARY}?jobId=${jobId}&candidateEmployerJobId=${candidateEmployerJobId}`)
            .then((response: AxiosResponse<IBaseResponse<IApplicantSummary>>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const getFingerprintImage = createAsyncThunk<string, { applicantId: number }, { rejectValue: IFailureResponse }>(
    'getFingerprintImage',
    async ({ applicantId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${ENDPOINTS.APPLICANTS}/${applicantId}/fingerprints/baseball`)
            .then((response: AxiosResponse<string>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const changeApplicantStage = createAsyncThunk<IBaseResponse, IApplicantStageParams, { rejectValue: IBaseResponse }>(
    'applicantStage',
    async ({ applicantId, payload }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${ENDPOINTS.APPLICANTS}/${applicantId}/stage`, payload)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

//update applicant rating
export const updateRating = createAsyncThunk<IBaseResponse, IApplicantRatingUpdate, { rejectValue: IBaseResponse }>(
    'updateRating',
    async ({ applicantId, payload }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${ENDPOINTS.APPLICANTS}/${applicantId}/rating`, payload)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

const applicantsListSlice = createSlice({
    name: 'applicantsList',
    initialState: initialApplicantsState,
    reducers: {
        toggleApplicantSummary: (state, action: PayloadAction<number>) => {
            state.applicantsList[action.payload].isExpanded = !state.applicantsList[action.payload].isExpanded;
        },
        resetApplicantsState: (state) => {
            state = { ...initialApplicantsState, currentApplicantProfile: state.currentApplicantProfile };
        },
        resetStageChange: (state, action: PayloadAction<number>) => {
            const indx = state.applicantsList.findIndex(aplcnt => aplcnt.candidateEmployerJobId === action.payload);
            state.applicantsList[indx].changeStateStatus = 'idle';
            state.applicantsList[indx].changeStateResponse = '';
        },
        resetRatingChange: (state, action: PayloadAction<number>) => {
            const indx = state.applicantsList.findIndex(aplcnt => aplcnt.candidateEmployerJobId === action.payload);
            state.applicantsList[indx].updateRatingStatus = 'idle';
            state.applicantsList[indx].updateRatingResponse = '';
        },
        setCurrentApplicantProfile: (state, action: PayloadAction<IApplicant>) => {
            state.currentApplicantProfile = action.payload;
        }
    },
    extraReducers: (builder) => {
        // fetch applicants list
        builder.addCase(fetchApplicantsList.pending, (state) => {
            state.getAppliListStatus = 'pending'
        });
        builder.addCase(fetchApplicantsList.fulfilled, (state, action) => {
            state.getAppliListStatus = 'success';
            state.applicantsList = action?.payload.data;
            state.paginationParams = action?.payload.stats
        });
        builder.addCase(fetchApplicantsList.rejected, (state, action) => {
            state.getAppliListStatus = 'failed';
            state.getAppliListResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        // fetch applicant summary
        builder.addCase(fetchApplicantSummary.pending, (state, action) => {
            if (!(action.meta.arg.candidateEmployerJobId in state.applicantsSummary)) {
                state.applicantsSummary[action.meta.arg.candidateEmployerJobId] = { getSummaryStatus: 'pending' };
            }
            state.applicantsSummary[action.meta.arg.candidateEmployerJobId].getSummaryStatus = 'pending';
            const appliIndx = state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.candidateEmployerJobId)
            if (appliIndx >= 0) {
                state.applicantsList[appliIndx].isExpanded = true;
            }
            // For applicant profile page.
            state.getApplicantSummaryStatus = 'pending';
        });
        builder.addCase(fetchApplicantSummary.fulfilled, (state, action) => {
            state.applicantsSummary[action.meta.arg.candidateEmployerJobId] = {
                ...action?.payload.data,
                getSummaryStatus: 'success',
                baseballCardUrl: ''
            };
            // For applicant profile page.
            state.getApplicantSummaryStatus = 'success';
            state.applicantSummary = action?.payload?.data;
        });
        builder.addCase(fetchApplicantSummary.rejected, (state, action) => {
            state.applicantsSummary[action.meta.arg.candidateEmployerJobId].getSummaryStatus = 'failed';
            state.applicantsSummary[action.meta.arg.candidateEmployerJobId].getSummaryResponse = action?.payload?.message;
            // For applicant profile page.
            state.getApplicantSummaryStatus = 'failed';
            state.applicantSummary = undefined;
            state.getApplicantSummaryResponse = action?.payload?.message;
        });
        // fetch applicant fingerprint image
        builder.addCase(getFingerprintImage.pending, (state, action) => {
            if (state.applicantsSummary[action.meta.arg.applicantId]) {
                state.applicantsSummary[action.meta.arg.applicantId].getFingerprintImageStatus = 'pending';
            }
            // For applicant profile page.
            state.getApplicantFingerPrintImageStatus = 'pending';
        });
        builder.addCase(getFingerprintImage.fulfilled, (state, action) => {
            if (state.applicantsSummary[action.meta.arg.applicantId]) {
                state.applicantsSummary[action.meta.arg.applicantId].getFingerprintImageStatus = 'success';
                state.applicantsSummary[action.meta.arg.applicantId].baseballCardUrl = action.payload;
            }
            // For applicant profile page.
            state.getApplicantFingerPrintImageStatus = 'success';
            state.applicantFingerPrintImageUrl = action.payload;
        });
        builder.addCase(getFingerprintImage.rejected, (state, action) => {
            if (state.applicantsSummary[action.meta.arg.applicantId]) {
                state.applicantsSummary[action.meta.arg.applicantId].getFingerprintImageStatus = 'failed';
                state.applicantsSummary[action.meta.arg.applicantId].getFingerprintImageResponse = action?.payload?.message;
            }
            // For applicant profile page.
            state.getApplicantFingerPrintImageStatus = 'failed';
            state.getApplicantFingerPrintImageResponse = '';
            state.applicantFingerPrintImageUrl = undefined;
        });
        // change applicant stage
        builder.addCase(changeApplicantStage.pending, (state, action) => {
            if ([state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId)]) {
                // Get index from applicants list with applicant ID in params and update state.
                state.applicantsList[state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId)].changeStateStatus = 'pending';
            }
        });
        builder.addCase(changeApplicantStage.fulfilled, (state, action) => {
            const indx = state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId);
            if (state.applicantsList[indx]) {
                state.applicantsList[indx].changeStateStatus = 'success';
                state.applicantsList[indx].changeStateResponse = action?.payload?.message;
                state.applicantsList[indx].jobStage = action.meta.arg.payload.stage;
            }

        });
        builder.addCase(changeApplicantStage.rejected, (state, action) => {
            const indx = state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId);
            if (state.applicantsList[indx]) {
                state.applicantsList[indx].changeStateStatus = 'failed';
                state.applicantsList[indx].changeStateResponse = action?.payload?.message;
            }
        });
        // update rating
        builder.addCase(updateRating.pending, (state, action) => {
            if (state.applicantsList[state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId)]) {
                // Get index from applicants list with applicant ID in params and update state.
                state.applicantsList[state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId)].updateRatingStatus = 'pending';
            }
        });
        builder.addCase(updateRating.fulfilled, (state, action) => {
            const indx = state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId);
            if (state.applicantsList[indx]) {
                state.applicantsList[indx].updateRatingStatus = 'success';
                state.applicantsList[indx].updateRatingResponse = action?.payload?.message;
                state.applicantsList[indx].rating = action.meta.arg.payload.rating;
            }

        });
        builder.addCase(updateRating.rejected, (state, action) => {
            const indx = state.applicantsList.findIndex(al => al.candidateEmployerJobId === action.meta.arg.applicantId);
            if (state.applicantsList[indx]) {
                state.applicantsList[indx].updateRatingStatus = 'failed';
                state.applicantsList[indx].updateRatingResponse = action?.payload?.message;
            }
        });
    }
});

export const { toggleApplicantSummary, resetApplicantsState, resetStageChange,
    setCurrentApplicantProfile, resetRatingChange } = applicantsListSlice.actions;
export default applicantsListSlice;
